/**
 * /plaza/demo — Thursday Investor Demo-Day Guide
 *
 * A single-page playbook for the Thursday 9:30 AM investor walkthrough.
 * Lists every demo in the chronological order they should be opened, with
 * a brief rationale + duration estimate for each step.
 *
 * Notes on the future:
 *   • When the production deploy goes live on hustleplaza.au, this page
 *     should be gated behind a login (so the dropshipper/investors can
 *     reach it but the public marketplace at /plaza stays open).
 *   • For now, a soft "Internal" banner reminds the team this is a private
 *     playbook URL — share with care.
 */
import { useEffect, useState } from "react";
import {
  Sun, Moon, ExternalLink, Lock, ArrowRight, Clock,
} from "lucide-react";
import { DEMOS } from "@/lib/demos";
import "./PlazaDemoPage.css";

const LOGO_LIGHT =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";
const LOGO_DARK =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/ecrffi9b_Local%20Hustle%20Logo%20-%20PLAZA%20DARK.png";

const THEME_KEY = "hpz-theme"; // shares the toggle state with /plaza

/* Demo flow loaded from /lib/demos — keeps /demo and /plate in sync. */

export default function PlazaDemoPage() {
  /* Theme shared with /plaza via localStorage. */
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem(THEME_KEY) || "dark";
  });
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  /* Total run-time so the team can pace the room. */
  const totalMinutes = DEMOS.reduce((s, d) => s + parseInt(d.duration, 10), 0);

  return (
    <div className="pdemo-root" data-theme={theme} data-testid="plaza-demo-root">
      {/* ── Top Bar ─────────────────────────────────────────────── */}
      <div className="pdemo-topbar">
        <a href="/plaza" className="pdemo-topbar__brand">
          <img
            src={theme === "light" ? LOGO_LIGHT : LOGO_DARK}
            alt="HustlePlaza"
            className="pdemo-topbar__logo"
          />
        </a>
        <div className="pdemo-topbar__right">
          <span className="pdemo-private" data-testid="plaza-demo-private-pill">
            <Lock size={12} /> Internal · Playbook
          </span>
          <div className="pdemo-theme-toggle" role="group" aria-label="Colour mode">
            <button
              className={theme === "light" ? "on" : ""}
              onClick={() => setTheme("light")}
              title="Light mode"
              aria-label="Switch to light mode"
              data-testid="plaza-demo-theme-light"
            >
              <Sun size={16} />
            </button>
            <button
              className={theme === "dark" ? "on" : ""}
              onClick={() => setTheme("dark")}
              title="Dark mode"
              aria-label="Switch to dark mode"
              data-testid="plaza-demo-theme-dark"
            >
              <Moon size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="pdemo-hero">
        <span className="pdemo-pill">
          <Clock size={12} /> THURSDAY · 09:30 · INVESTOR WALKTHROUGH
        </span>
        <h1>The Demo-Day <em>Playbook.</em></h1>
        <p className="pdemo-sub">
          Open each link in a new tab in the order shown. Estimated total
          run-time: <strong>{totalMinutes} minutes</strong> (≈ {Math.ceil(totalMinutes / 5) * 5} mins
          with Q&A). Each card explains <em>why</em> we're showing this in this
          order, and which beat to land on.
        </p>
        <div className="pdemo-meta">
          <span><strong>{DEMOS.length}</strong> stops</span>
          <span><strong>~{totalMinutes}</strong> min</span>
          <span><strong>1</strong> tight narrative</span>
        </div>
      </section>

      {/* ── Demo cards ──────────────────────────────────────────── */}
      <section className="pdemo-list">
        {DEMOS.map((d, idx) => {
          const Icon = d.icon;
          return (
            <article
              key={d.n}
              className={`pdemo-card ${d.featured ? "pdemo-card--featured" : ""}`}
              data-testid={`plaza-demo-card-${d.n}`}
            >
              <div className="pdemo-card__num">{String(d.n).padStart(2, "0")}</div>
              <div className="pdemo-card__icon"><Icon size={22} /></div>
              <div className="pdemo-card__body">
                <div className="pdemo-card__head">
                  <span className="pdemo-card__type">{d.type}</span>
                  <span className="pdemo-card__dur"><Clock size={12} /> {d.duration}</span>
                </div>
                <h3 className="pdemo-card__title">{d.title}</h3>
                <p className="pdemo-card__why">{d.why}</p>
                {d.note && <p className="pdemo-card__note">↳ {d.note}</p>}
              </div>
              <div className="pdemo-card__cta">
                <a
                  href={d.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdemo-btn"
                  data-testid={`plaza-demo-open-${d.n}`}
                >
                  Open <ExternalLink size={14} />
                </a>
                <span className="pdemo-card__url">{d.to}</span>
              </div>
              {idx < DEMOS.length - 1 && <div className="pdemo-thread" aria-hidden="true" />}
            </article>
          );
        })}
      </section>

      {/* ── Future-gating note ──────────────────────────────────── */}
      <section className="pdemo-footnote" data-testid="plaza-demo-future-gating">
        <div className="pdemo-footnote__icon"><Lock size={20} /></div>
        <div>
          <h4>After deploy: this page goes behind a login.</h4>
          <p>
            When we publish <strong>hustleplaza.au</strong>, the public marketplace
            at <code>/plaza</code> stays open to everyone — but this playbook page
            (<code>/plaza/demo</code>) will require an internal login so investors,
            advisors, and the team can reach it without the URL leaking. Hand-off
            ready: drop an auth-required wrapper around this component when we
            wire up the production auth layer.
          </p>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="pdemo-foot">
        <p>
          HustlePlate — Demo Day Playbook · © 2026 ·
          {" "}<a href="/plaza" data-testid="plaza-demo-back-plaza">Back to HustlePlaza</a>
          {" · "}<a href="/" data-testid="plaza-demo-back-home">HustlePlate home</a>
        </p>
      </footer>
    </div>
  );
}
